import { useState } from 'react';
import HolographicCard from '@/components/base/HolographicCard';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Convert form data to URL-encoded format
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d586gt93kamldd155uh0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-32 overflow-hidden">
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold mb-6 font-orbitron">
              <span 
                className="text-white"
                style={{
                  textShadow: `
                    -1px -1px 0 #8b5cf6,
                    1px -1px 0 #8b5cf6,
                    -1px 1px 0 #8b5cf6,
                    1px 1px 0 #8b5cf6,
                    -2px -2px 0 #06b6d4,
                    2px -2px 0 #06b6d4,
                    -2px 2px 0 #06b6d4,
                    2px 2px 0 #06b6d4
                  `
                }}
              >
                Contact Fieldscale
              </span>
            </h2>
            <p className="text-xl text-white/80 font-light">
              Questions about GenesisOS, Precursor, or partnership opportunities? We&apos;d love to hear from you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <HolographicCard className="p-8">
                <h3 className="text-2xl font-bold text-white mb-8">Get in Touch</h3>
                
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-400/20 to-cyan-600/20 rounded-lg flex items-center justify-center border border-cyan-400/30">
                      <i className="ri-mail-line text-cyan-400 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-white font-medium">Email</p>
                      <p className="text-white/70 text-sm">Contact via form</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-br from-purple-400/20 to-purple-600/20 rounded-lg flex items-center justify-center border border-purple-400/30">
                      <i className="ri-map-pin-line text-purple-400 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-white font-medium">Location</p>
                      <p className="text-white/70 text-sm">71-75 Shelton Street, Covent Garden</p>
                      <p className="text-white/70 text-sm">London, United Kingdom, WC2H 9JQ</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-400/20 to-purple-400/20 rounded-lg flex items-center justify-center border border-cyan-400/30">
                      <i className="ri-time-line text-cyan-400 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-white font-medium">Response Time</p>
                      <p className="text-white/70 text-sm">Within 24 hours</p>
                    </div>
                  </div>
                </div>
              </HolographicCard>
            </div>

            <div>
              <HolographicCard className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-white/80 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-white/30 transition-colors text-sm"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-white/30 transition-colors text-sm"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-white/80 mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-white/30 transition-colors text-sm"
                      placeholder="How can we help?"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-white/80 mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-white/30 transition-colors resize-none text-sm"
                      placeholder="Tell us about your inquiry..."
                    />
                    <p className="text-xs text-white/40 mt-1">{formData.message.length}/500 characters</p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <p className="text-green-400 text-sm">Message sent successfully! We'll get back to you soon.</p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-red-400 text-sm">Failed to send message. Please try again.</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-gradient-to-r from-cyan-400 to-purple-500 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-cyan-400/25 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </HolographicCard>
            </div>
          </div>
        </div>
      </div>

      {/* Background elements */}
      <div className="absolute bottom-20 left-1/4 w-32 h-32 bg-gradient-to-br from-cyan-400/5 to-transparent rounded-full blur-2xl"></div>
      <div className="absolute top-20 right-1/4 w-40 h-40 bg-gradient-to-br from-purple-400/5 to-transparent rounded-full blur-2xl"></div>
    </section>
  );
}
